import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { 
  ChevronDown, ChevronUp, Wand2, Settings2, Loader2,
  Mic, Volume2, Music2, Waves, Sparkles, Zap,
  Radio, Headphones, Crown, Lock, Coins, Play, Pause,
  Eye, Check, X, Undo2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// EQ Presets with tier access
const EQ_PRESETS = {
  warm: { label: "Warm", description: "Full-bodied sound", tier: "starter" },
  podcast: { label: "Podcast", description: "Clear voice focus", tier: "starter" },
  bass_boost: { label: "Bass Boost", description: "Enhanced low-end", tier: "starter" },
  background: { label: "Background", description: "Subtle ambient", tier: "starter" },
  bright: { label: "Bright", description: "Crisp & airy", tier: "pro" },
  flat: { label: "Flat", description: "Neutral", tier: "pro" }
};

// Effects with tier access
const EFFECTS = {
  reverb: { label: "Reverb", description: "Room ambience", tier: "pro", credits: 3 },
  compression: { label: "Compression", description: "Dynamic control", tier: "pro", credits: 3 },
  noise_reduction: { label: "Noise Reduction", description: "Clean up audio", tier: "pro", credits: 3 },
  delay: { label: "Delay / Echo", description: "Rhythmic echo", tier: "elite", credits: 4 },
  stereo: { label: "Stereo Widening", description: "Spacious sound", tier: "elite", credits: 4 }
};

// AI Smart Tools
const AI_TOOLS = [
  { id: "optimize_voiceover", label: "Optimize for Voiceover", description: "Lower & duck for narration", icon: Mic, credits: 5 },
  { id: "make_cinematic", label: "Make Cinematic", description: "Fuller, more dramatic", icon: Music2, credits: 5 },
  { id: "soften_background", label: "Soften Background", description: "Subtle ambient music", icon: Volume2, credits: 5 },
  { id: "perfect_loop", label: "Perfect Loop", description: "Seamless looping", icon: Radio, credits: 5 },
  { id: "auto_balance", label: "Auto-Balance Levels", description: "Normalize audio", icon: Waves, credits: 5 }
];

const StudioToolsDrawer = ({ 
  musicId, 
  onProcessed, 
  disabled = false 
}) => {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('eq');
  const [processing, setProcessing] = useState(null);
  const [intensity, setIntensity] = useState(0.5);
  const [creditBalance, setCreditBalance] = useState(null);
  
  // Preview before apply state
  const [previewMode, setPreviewMode] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [pendingEffect, setPendingEffect] = useState(null);
  const previewAudioRef = useRef(null);
  
  // Undo state for credit usage toast
  const [lastAppliedEffect, setLastAppliedEffect] = useState(null);
  const [undoTimeout, setUndoTimeout] = useState(null);
  
  const tier = user?.tier || 'starter';
  const isFounder = user?.is_founder;

  // Fetch credit balance when drawer opens
  const fetchCredits = async () => {
    try {
      const response = await axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditBalance(response.data);
    } catch (e) {
      console.error('Failed to fetch credits:', e);
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchCredits();
    }
  };

  const canAccessTier = (requiredTier) => {
    if (isFounder) return true;
    const tierOrder = ['starter', 'standard', 'pro', 'elite', 'agency', 'studio'];
    return tierOrder.indexOf(tier) >= tierOrder.indexOf(requiredTier);
  };

  // Generate preview without applying
  const generatePreview = async (effect) => {
    if (!musicId || !token) return;
    
    setProcessing(`preview_${effect}`);
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/preview-effect`,
        {
          music_id: musicId,
          effect: effect,
          intensity: intensity,
          preview_duration: 10
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setPreviewData(response.data.preview);
        setPendingEffect(effect);
        toast.success('Preview generated - no credits used yet');
        
        // Auto-play preview
        if (previewAudioRef.current) {
          previewAudioRef.current.src = `${process.env.REACT_APP_BACKEND_URL}${response.data.preview.preview_url}`;
          previewAudioRef.current.play();
          setPreviewPlaying(true);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setProcessing(null);
    }
  };

  // Apply effect (with credits)
  const applyEffect = async (effect, isEQ = false) => {
    if (!musicId || !token) return;
    
    const effectInfo = isEQ ? { tier: EQ_PRESETS[effect]?.tier || 'starter', credits: 0 } : EFFECTS[effect];
    
    if (!isEQ && !canAccessTier(effectInfo.tier)) {
      toast.error(`${effectInfo.label || effect} requires ${effectInfo.tier.charAt(0).toUpperCase() + effectInfo.tier.slice(1)} subscription`);
      return;
    }
    
    // Check credits
    if (!isEQ && !isFounder && creditBalance && creditBalance.balance < effectInfo.credits) {
      toast.error(`Not enough credits. Need ${effectInfo.credits}, have ${creditBalance.balance}`);
      return;
    }
    
    setProcessing(effect);
    try {
      const effectKey = isEQ ? `eq_${effect}` : effect;
      const response = await axios.post(
        `${API}/music/${musicId}/apply-effect?effect=${effectKey}&intensity=${intensity}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const creditsUsed = response.data.credits_used || 0;
        
        // Clear preview state
        setPreviewData(null);
        setPendingEffect(null);
        if (previewAudioRef.current) {
          previewAudioRef.current.pause();
          setPreviewPlaying(false);
        }
        
        // Show credit usage toast with undo option
        if (creditsUsed > 0) {
          showCreditUsageToast(creditsUsed, effectInfo?.label || effect, response.data.processed);
        } else {
          toast.success(`Applied ${isEQ ? EQ_PRESETS[effect]?.label : effectInfo?.label || effect}`);
        }
        
        onProcessed?.(response.data.processed);
        fetchCredits();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply effect');
    } finally {
      setProcessing(null);
    }
  };

  // Show credit usage toast with undo option (10 seconds)
  const showCreditUsageToast = (credits, effectName, processedData) => {
    // Clear any existing timeout
    if (undoTimeout) {
      clearTimeout(undoTimeout);
    }
    
    setLastAppliedEffect({ credits, effectName, processedData });
    
    toast.custom((t) => (
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 shadow-lg flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
          <Coins className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-white">{credits} credits used</p>
          <p className="text-sm text-zinc-400">{effectName} applied</p>
        </div>
        <button
          onClick={() => {
            toast.dismiss(t);
            // Note: Full undo would require backend support to revert
            toast.info('Effect applied - create a new version to try different settings');
          }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 hover:text-white text-sm transition-colors"
        >
          <Undo2 className="w-4 h-4" />
          Info
        </button>
      </div>
    ), { duration: 10000 });
    
    // Set timeout to clear undo state
    const timeout = setTimeout(() => {
      setLastAppliedEffect(null);
    }, 10000);
    setUndoTimeout(timeout);
  };

  const applyEQPreset = async (preset) => {
    if (previewMode && !pendingEffect) {
      // Generate preview first if preview mode is on
      await generatePreview(`eq_${preset}`);
    } else {
      // Apply directly
      await applyEffect(preset, true);
    }
  };

  const applyEffectWithPreview = async (effect) => {
    if (previewMode && !pendingEffect) {
      // Generate preview first
      await generatePreview(effect);
    } else {
      // Apply directly
      await applyEffect(effect, false);
    }
  };

  // Confirm and apply pending effect
  const confirmApply = async () => {
    if (pendingEffect) {
      const isEQ = pendingEffect.startsWith('eq_');
      const effectKey = isEQ ? pendingEffect.replace('eq_', '') : pendingEffect;
      await applyEffect(effectKey, isEQ);
    }
  };

  // Cancel preview
  const cancelPreview = () => {
    setPreviewData(null);
    setPendingEffect(null);
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      setPreviewPlaying(false);
    }
  };

  // Toggle preview playback
  const togglePreviewPlayback = () => {
    if (previewAudioRef.current) {
      if (previewPlaying) {
        previewAudioRef.current.pause();
      } else {
        previewAudioRef.current.play();
      }
      setPreviewPlaying(!previewPlaying);
    }
  };

  const applyAITool = async (toolId) => {
    if (!musicId || !token) return;
    
    const tool = AI_TOOLS.find(t => t.id === toolId);
    if (!tool) return;
    
    // AI tools require Pro+
    if (!canAccessTier('pro')) {
      toast.error('AI audio tools require Pro+ subscription');
      return;
    }
    
    // Check credits
    if (!isFounder && creditBalance && creditBalance.balance < tool.credits) {
      toast.error(`Not enough credits. Need ${tool.credits}, have ${creditBalance.balance}`);
      return;
    }
    
    setProcessing(toolId);
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/ai-enhance?tool=${toolId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const creditsUsed = response.data.credits_used || tool.credits;
        
        if (creditsUsed > 0 && !isFounder) {
          showCreditUsageToast(creditsUsed, tool.label, response.data.processed);
        } else {
          toast.success(`Applied: ${tool.label}`);
        }
        
        onProcessed?.(response.data.processed);
        fetchCredits();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply enhancement');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="collapsible-panel">
      {/* Hidden audio element for preview playback */}
      <audio 
        ref={previewAudioRef} 
        onEnded={() => setPreviewPlaying(false)}
        onPause={() => setPreviewPlaying(false)}
        onPlay={() => setPreviewPlaying(true)}
      />
      
      <button
        onClick={handleOpen}
        className="collapsible-trigger"
        disabled={disabled}
        data-testid="studio-tools-toggle"
      >
        <span className="flex items-center gap-2">
          <Settings2 className="w-4 h-4" />
          Open Studio Tools
          {!isFounder && creditBalance && (
            <span className="ml-2 text-xs px-2 py-0.5 bg-primary/20 text-primary rounded-full flex items-center gap-1">
              <Coins className="w-3 h-3" />
              {creditBalance.balance}
            </span>
          )}
        </span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
      
      {isOpen && (
        <div className="collapsible-content animate-fade-in-up space-y-6">
          {/* Preview Mode Toggle */}
          <div className="flex items-center justify-between p-3 bg-zinc-900/60 border border-zinc-800 rounded-xl">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-primary" />
              <div>
                <p className="font-medium text-sm">Preview before apply</p>
                <p className="text-xs text-zinc-500">Hear effects before using credits</p>
              </div>
            </div>
            <Switch
              checked={previewMode}
              onCheckedChange={setPreviewMode}
              data-testid="preview-mode-toggle"
            />
          </div>
          
          {/* Pending Preview Banner */}
          {previewData && pendingEffect && (
            <div className="p-4 bg-primary/10 border border-primary/30 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={togglePreviewPlayback}
                    className="w-10 h-10 rounded-full bg-primary flex items-center justify-center"
                  >
                    {previewPlaying ? (
                      <Pause className="w-5 h-5 text-white" />
                    ) : (
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    )}
                  </button>
                  <div>
                    <p className="font-medium text-sm">Preview: {pendingEffect}</p>
                    <p className="text-xs text-zinc-400">No credits used yet</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={confirmApply}
                  className="flex-1 bg-primary hover:bg-primary/90"
                  disabled={processing}
                  data-testid="apply-preview-btn"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Apply Effect
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={cancelPreview}
                  data-testid="cancel-preview-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          {/* Tab Navigation */}
          <div className="flex gap-2 border-b border-zinc-800 pb-3">
            {['eq', 'effects', 'ai'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab
                    ? 'bg-primary/20 text-primary'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                {tab === 'eq' && 'EQ Presets'}
                {tab === 'effects' && 'Effects'}
                {tab === 'ai' && (
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-4 h-4" />
                    AI Smart
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Intensity Slider (for effects) */}
          {(activeTab === 'effects') && (
            <div className="space-y-2">
              <Label className="text-zinc-400 flex justify-between">
                <span>Intensity</span>
                <span className="text-primary">{Math.round(intensity * 100)}%</span>
              </Label>
              <Slider
                value={[intensity]}
                onValueChange={(v) => setIntensity(v[0])}
                min={0}
                max={1}
                step={0.1}
              />
            </div>
          )}

          {/* EQ Presets Tab */}
          {activeTab === 'eq' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(EQ_PRESETS).map(([key, preset]) => {
                const canAccess = canAccessTier(preset.tier);
                const isProcessing = processing === `eq_${key}` || processing === `preview_eq_${key}`;
                return (
                  <button
                    key={key}
                    onClick={() => canAccess && applyEQPreset(key)}
                    disabled={!canAccess || isProcessing || disabled}
                    className={`relative p-4 rounded-xl border text-left transition-all ${
                      canAccess
                        ? 'bg-zinc-900/50 border-zinc-800 hover:border-primary/50 hover:bg-zinc-800/50'
                        : 'bg-zinc-900/30 border-zinc-800/50 opacity-60'
                    }`}
                    data-testid={`eq-preset-${key}`}
                  >
                    {!canAccess && (
                      <Lock className="absolute top-2 right-2 w-4 h-4 text-zinc-500" />
                    )}
                    {isProcessing && (
                      <Loader2 className="absolute top-2 right-2 w-4 h-4 text-primary animate-spin" />
                    )}
                    <p className="font-medium text-sm">{preset.label}</p>
                    <p className="text-xs text-zinc-500 mt-1">{preset.description}</p>
                    {preset.tier !== 'starter' && (
                      <span className={`mt-2 inline-block text-xs px-2 py-0.5 rounded ${
                        preset.tier === 'pro' ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {preset.tier.toUpperCase()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Effects Tab */}
          {activeTab === 'effects' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(EFFECTS).map(([key, effect]) => {
                  const canAccess = canAccessTier(effect.tier);
                  const isProcessing = processing === key || processing === `preview_${key}`;
                  return (
                    <button
                      key={key}
                      onClick={() => canAccess && applyEffectWithPreview(key)}
                      disabled={!canAccess || isProcessing || disabled}
                      className={`relative p-4 rounded-xl border text-left transition-all ${
                        canAccess
                          ? 'bg-zinc-900/50 border-zinc-800 hover:border-primary/50 hover:bg-zinc-800/50'
                          : 'bg-zinc-900/30 border-zinc-800/50 opacity-60'
                      }`}
                      data-testid={`effect-${key}`}
                    >
                      {!canAccess && (
                        <Lock className="absolute top-2 right-2 w-4 h-4 text-zinc-500" />
                      )}
                      {isProcessing && (
                        <Loader2 className="absolute top-2 right-2 w-4 h-4 text-primary animate-spin" />
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{effect.label}</p>
                          <p className="text-xs text-zinc-500 mt-1">{effect.description}</p>
                        </div>
                        {canAccess && (
                          <div className="flex items-center gap-1 text-xs text-primary">
                            <Coins className="w-3 h-3" />
                            {effect.credits}
                          </div>
                        )}
                      </div>
                      <span className={`mt-2 inline-block text-xs px-2 py-0.5 rounded ${
                        effect.tier === 'pro' ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {effect.tier.toUpperCase()}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Smart Tools Tab */}
          {activeTab === 'ai' && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                One-click AI enhancements. Uses {AI_TOOLS[0]?.credits || 5} credits each.
              </p>
              
              {!canAccessTier('pro') && (
                <div className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl text-purple-400 text-sm">
                  <Crown className="w-5 h-5" />
                  <span>AI audio tools require Pro+ subscription</span>
                </div>
              )}
              
              <div className="flex flex-wrap gap-2">
                {AI_TOOLS.map((tool) => {
                  const Icon = tool.icon;
                  const canAccess = canAccessTier('pro');
                  return (
                    <button
                      key={tool.id}
                      onClick={() => canAccess && applyAITool(tool.id)}
                      disabled={!canAccess || processing === tool.id || disabled}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-all ${
                        canAccess
                          ? 'bg-zinc-900/50 border-zinc-700 hover:border-primary hover:bg-primary/10 text-white'
                          : 'bg-zinc-900/30 border-zinc-800/50 text-zinc-500'
                      }`}
                      data-testid={`ai-tool-${tool.id}`}
                    >
                      {processing === tool.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Icon className="w-4 h-4" />
                      )}
                      {tool.label}
                      {canAccess && (
                        <span className="text-xs text-primary flex items-center gap-0.5">
                          <Coins className="w-3 h-3" />
                          {tool.credits}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {isFounder && (
                <p className="text-xs text-amber-400 flex items-center gap-1">
                  <Crown className="w-3 h-3" />
                  Founder: No credit deduction
                </p>
              )}
            </div>
          )}

          {/* Credit Info */}
          {!isFounder && creditBalance && creditBalance.balance < 10 && (
            <div className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <span className="text-sm text-amber-400">Low credits</span>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-black"
                onClick={() => window.location.href = '/billing'}
              >
                <Zap className="w-4 h-4 mr-1" />
                Get More
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudioToolsDrawer;
